/**
 * MagicPosterPanel - 魔法海报面板主组件
 * 组装 StepTabs、FileSelector、Toolbar、Editor/PosterPreview 等子组件
 * 业务逻辑通过 useMagicPosterPanel hook 管理
 */
import React, { memo, useRef, useMemo } from 'react';
import { useSize } from 'ahooks';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { ReportStepTabs } from '../../../components/ReportViewer/ReportStepTabs';
import { ReportFileSelector } from '../../../components/ReportViewer/ReportFileSelector';
import { ReportToolbar } from '../../../components/ReportViewer/ReportToolbar';
import { ReportEditor } from '../../../components/ReportViewer/ReportEditor';
import { MarkdownEditor } from '../../components';
import { MainContentCmp } from '../../../ChatPage/components/SubComp';
import { PosterStyleSelector } from './components/PosterStyleSelector';
import { PosterPreviewPanel } from './components/PosterPreviewPanel';
import { ChartShareModal } from '../../../ChatShare/ChatShareModal';
import { useMagicPosterPanel } from './hooks/useMagicPosterPanel';
import { IMagicPosterPanelProps, EPosterActions } from './types';
import {
  POSTER_STEP_TABS_CONFIG,
  POSTER_STEP_TYPE,
  POSTER_EXPORT_FILE_TYPE_CONFIG,
  getPosterToolbarActions,
  POSTER_FILE_SUFFIX,
} from './constants';
import magicPosterFileIcon from '@/pages/aigc/assets/svg/icon-magic-poster-file.svg';
// 魔法海报图标（与 dba-chart-document 卡片保持一致）
import magicPosterMdIcon from '@/pages/aigc/assets/svg/file-format-markdown.svg';
import magicPosterPngIcon from '@/pages/aigc/assets/svg/agent-document-magic-poster.svg';

import './MagicPosterPanel.scss';

/**
 * 自定义 memo 比较函数：只比较 MagicPosterPanel 真正关注的 props
 * - sessionId / dsReport.id：会话/文件变化需要完整刷新
 * - streamingState：流式状态影响按钮禁用和 loading 派生
 * - conversations 最后一轮 answer 长度：新文档（如 png_file）出现时需重渲染以取消 loading
 */
const areMagicPosterPropsEqual = (prevProps: any, nextProps: any): boolean => {
  const prevLastAnswerLen = prevProps.conversations?.[prevProps.conversations?.length - 1]?.answer?.length ?? 0;
  const nextLastAnswerLen = nextProps.conversations?.[nextProps.conversations?.length - 1]?.answer?.length ?? 0;
  return (
    prevProps.sessionId === nextProps.sessionId &&
    prevProps.dsReport?.id === nextProps.dsReport?.id &&
    prevProps.dsReport?.file_suffix === nextProps.dsReport?.file_suffix &&
    prevProps.streamingState === nextProps.streamingState &&
    prevLastAnswerLen === nextLastAnswerLen
  );
};

export const MagicPosterPanel = memo(({
  sessionId,
  conversations,
  dsReport,
  streamingState,
  handleUpdateDsReport,
  startNewConversation,
  readonly = false,
  showCloseButton = true,
  className,
  onHeaderMouseDown,
  onFocus,
}: IMagicPosterPanelProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth = 0 } = useSize(containerRef) || {};
  const { locale } = useIntl();
  const isEn = locale === 'en-US';

  // 使用业务逻辑 hook
  const {
    stepActive,
    fileList,
    currentFile,
    loading,
    isDisabledPosterTab,
    selectedStyle,
    isStyleValid,
    markdownRef,
    reportRef,
    handleStepClick,
    handleFileItemClick,
    handleAction,
    handleExport,
    handleStyleChange,
    currentFileContent,
    currentFileSignedUrl,
    latestPosterCosUrl,
    reusedPosterUrl,
    posterLoading,
    showShareChatDialog,
    setShowShareChatDialog,
    handleClose,
    refreshPosterUrl,
  } = useMagicPosterPanel({
    sessionId,
    conversations,
    dsReport,
    streamingState,
    handleUpdateDsReport,
    startNewConversation,
    readonly,
  });

  // 获取海报工具栏操作列表
  // readonly 模式下过滤掉重置、生成魔法海报、重新生成、分享按钮，只保留导出
  const posterActions = useMemo(() => {
    const allActions = getPosterToolbarActions(isEn, streamingState, isStyleValid, posterLoading);
    if (!readonly) return allActions;
    // 只读模式下仅保留导出按钮
    return allActions.filter((action) => action.key === EPosterActions.Export);
  }, [isEn, streamingState, isStyleValid, readonly, posterLoading]);

  // 是否处于内容规划步骤
  const isFrameworkStep = stepActive === POSTER_STEP_TYPE.FRAMEWORK;
  // 是否处于海报预览步骤
  const isPosterStep = stepActive === POSTER_STEP_TYPE.POSTER;

  // 海报预览仅渲染图片类型文件的签名 URL：
  // 若当前文件是 MD（内容规划文件），不喂给 <img>，避免把 markdown 当图片解码导致「加载失败」。
  const isCurrentMdFile = currentFile?.file_suffix === POSTER_FILE_SUFFIX.MD
    || (currentFile?.cos_url as string)?.endsWith('.md');
  // 是否正在展示"最新生成的海报"：当前文件即最新海报，或文件列表尚未就绪（初始/生成中）。
  // 此时优先复用主对话框已生成的同一张图片(reusedPosterUrl，共享签名缓存)，保证左右一致且更稳定；
  // 仅当用户在版本下拉里切到了"历史海报"(cos_url 不同)时，才回退到该文件自身的签名 URL。
  const isShowingLatestPoster = !currentFile?.cos_url || currentFile?.cos_url === latestPosterCosUrl;
  const posterImageUrl = isCurrentMdFile
    ? ''
    : (isShowingLatestPoster ? (reusedPosterUrl || currentFileSignedUrl) : currentFileSignedUrl);

  return (
    <div
      ref={containerRef}
      className={classNames('dba-magic-poster-panel', className)}
      onMouseDown={onFocus}
      onClick={onFocus}
    >
      {/* 头部区域 */}
      <div
        className={classNames('dba-magic-poster-panel__header', {
          'dba-magic-poster-panel__header--draggable': !!onHeaderMouseDown,
        })}
        onMouseDown={(e) => {
          onFocus?.();
          onHeaderMouseDown?.(e);
        }}
      >
        {/* 步骤页签 */}
        <ReportStepTabs
          activeStep={stepActive}
          isReportDisabled={isDisabledPosterTab}
          onStepChange={(step) => handleStepClick(step)}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClose={handleClose}
          showCloseButton={showCloseButton}
          stepsConfig={POSTER_STEP_TABS_CONFIG}
          disabledTip="请先生成魔法海报"
          disabledTipEn="Please generate a magic poster first"
        />

        {/* 工具栏区域 */}
        <div className={classNames('dba-magic-poster-panel__toolbar', {
          'dba-magic-poster-panel__toolbar--poster-step': isPosterStep,
        })}>
          {/* 文件选择器 */}
          <div className="dba-magic-poster-panel__toolbar-left">
            <ReportFileSelector
              fileList={fileList}
              currentFile={currentFile as any}
              activeFileKey={currentFile?.id || ''}
              containerWidth={containerWidth}
              activeStep={stepActive}
              triggerIcon={isPosterStep ? magicPosterFileIcon : undefined}
              overlayClassName="dba-magic-poster-file-selector-overlay"
              itemIconRenderer={(file) => {
                // 根据文件后缀类型显示不同图标（与 dba-chart-document 卡片保持一致）
                // 判断 MD 文件：file_suffix 为 md_file 或 cos_url 以 .md 结尾
                const isMdFile = file.file_suffix === POSTER_FILE_SUFFIX.MD
                  || (file.cos_url as string)?.endsWith('.md');
                return isMdFile ? magicPosterMdIcon : magicPosterPngIcon;
              }}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onFileSelect={handleFileItemClick}
            />
          </div>

          {/* 操作按钮 */}
          <div className="dba-magic-poster-panel__toolbar-right">
            <ReportToolbar
              activeStep={stepActive}
              streamingState={streamingState}
              onActionClick={handleAction}
              onExport={handleExport}
              customActions={posterActions}
              customExportConfig={POSTER_EXPORT_FILE_TYPE_CONFIG}
              exportActionKey={EPosterActions.Export}
              exportDisabled={posterLoading}
              disabledOnStreamingKeys={[EPosterActions.GeneratePoster, EPosterActions.Regenerate, EPosterActions.Share]}
            />
          </div>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="dba-magic-poster-panel__content">
        {/* 步骤1: 内容规划 - 编辑器 + 风格选择器（只读模式下隐藏风格选择器，保留编辑器） */}
        {isFrameworkStep && (
          <div className="dba-magic-poster-panel__framework">
            {/* 风格选择器（只读/分享模式下隐藏） */}
            {!readonly && (
              <PosterStyleSelector
                selectedStyle={selectedStyle}
                onStyleChange={handleStyleChange}
              />
            )}

            {/* 内容规划编辑器（readonly 时使用 MainContentCmp 只读展示） */}
            <ReportEditor
              content={currentFileContent}
              answer={dsReport?.answer}
              isLoading={loading}
              ref={readonly ? undefined : markdownRef}
              reportContainerRef={reportRef}
              MarkdownEditorComponent={(readonly ? MainContentCmp : MarkdownEditor) as any}
            />
          </div>
        )}

        {/* 步骤2: 海报预览 */}
        {isPosterStep && (
          <div className="dba-magic-poster-panel__poster">
            <PosterPreviewPanel
              imageUrl={posterImageUrl}
              // 已复用到主对话框生成的海报图时，不再被文件列表加载态阻塞，保证稳定展示
              isLoading={posterLoading && !(isShowingLatestPoster && reusedPosterUrl)}
              onRetry={refreshPosterUrl}
            />
          </div>
        )}
      </div>


      {/* 分享弹窗（只读模式下不展示） */}
      {!readonly && showShareChatDialog?.isShowCopyDialog && (
        <ChartShareModal
          showShareChatDialog={showShareChatDialog}
          setShowShareChatDialog={setShowShareChatDialog}
        />
      )}
    </div>
  );
}, areMagicPosterPropsEqual);

MagicPosterPanel.displayName = 'MagicPosterPanel';

export default MagicPosterPanel;
